import { useEffect, useMemo } from "react";

export function debounce<A = unknown, R = void>(
  fn: (args: A) => R,
  ms: number,
): [(args: A) => Promise<R>, () => void] {
  let timer: NodeJS.Timeout;

  const debouncedFunc = (args: A): Promise<R> =>
    new Promise((resolve) => {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        resolve(fn(args));
      }, ms);
    });

  const clear = () => clearTimeout(timer);

  return [debouncedFunc, clear];
}

function useDebounce<A = unknown, R = void>(
  fn: (args: A) => R,
  ms: number,
): (args: A) => Promise<R> {
  const [debouncedFn, clear] = useMemo(() => debounce<A, R>(fn, ms), [fn, ms]);

  useEffect(() => () => clear(), [clear]);

  return debouncedFn;
}

export default useDebounce;
